import {
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_MIN_LENGTH,
  normaliseDisplayName,
  validateDisplayName,
} from '@words-happen/display-name';
import { WordsHappenButton, WordsHappenTextField } from '@words-happen/ui-components';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import styles from './profile-panel.module.scss';

/** What the editor says when a save fails for a reason other than the name: the network or the API. */
export const DISPLAY_NAME_SAVE_FAILED_MESSAGE = "Your name wasn't saved. Check your connection and try again.";

/** Saves the normalised name; resolves to the message to show when the save fails, or undefined once it is saved. */
export type SaveDisplayName = (displayName: string) => Promise<string | undefined>;

export interface DisplayNameEditorProps {
  /** The text the field starts with. */
  initialValue: string;
  onSave: SaveDisplayName;
  /** Called once a save succeeds. */
  onSaved: () => void;
  /** Shows a Cancel button that calls it. Without it the editor stays open (no confirmed name yet). */
  onCancel?: () => void;
  saveLabel: string;
  /** Focuses the field when the editor opens, e.g. after Change. */
  autoFocus?: boolean;
}

export const DISPLAY_NAME_HINT = `${DISPLAY_NAME_MIN_LENGTH}–${DISPLAY_NAME_MAX_LENGTH} characters. Other players see this name.`;

/**
 * The display name field with Save (and Cancel when editing a confirmed name). Checks the name with the shared
 * validator, as words-happen-gql does. A pre-filled name that isn't valid shows why straight away; an empty one only
 * once the player tries to save. Saving shows a pending state; a failed save keeps the text and shows the error.
 */
export const DisplayNameEditor: React.FC<DisplayNameEditorProps> = ({
  initialValue,
  onSave,
  onSaved,
  onCancel,
  saveLabel,
  autoFocus = false,
}) => {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState(() => (initialValue ? validateDisplayName(initialValue) : undefined));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (autoFocus) {
      inputRef.current?.focus();
    }
    return () => {
      mounted.current = false;
    };
  }, [autoFocus]);

  const change = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    if (error) {
      setError(validateDisplayName(event.target.value));
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) {
      return;
    }

    const invalid = validateDisplayName(value);
    if (invalid) {
      setError(invalid);
      inputRef.current?.focus();
      return;
    }

    setError(undefined);
    setSaving(true);
    const failure = await onSave(normaliseDisplayName(value));
    if (!mounted.current) {
      return;
    }
    setSaving(false);
    if (failure) {
      setError(failure);
      inputRef.current?.focus();
    } else {
      onSaved();
    }
  };

  return (
    <form className={styles.editor} onSubmit={submit} noValidate>
      <WordsHappenTextField
        ref={inputRef}
        label="Display name"
        name="displayName"
        autoComplete="nickname"
        autoCapitalize="words"
        spellCheck={false}
        enterKeyHint="done"
        value={value}
        onChange={change}
        onBlur={(event) => {
          if (event.target.value !== initialValue) setError(validateDisplayName(event.target.value));
        }}
        hint={DISPLAY_NAME_HINT}
        error={error}
        readOnly={saving}
      />
      <div className={styles.editorActions}>
        <WordsHappenButton type="submit" variant="primary" size="sm" loading={saving} disabled={saving}>
          {saveLabel}
        </WordsHappenButton>
        {onCancel ? (
          <WordsHappenButton type="button" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </WordsHappenButton>
        ) : null}
      </div>
    </form>
  );
};
